import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { documentsAPI, formatApiErrorDetail } from '../../lib/api';
import { Plus, FileText, Upload, Download, Eye } from '@phosphor-icons/react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

const DOCUMENT_TYPES = ['constitution', 'decree', 'letter_patent', 'certificate', 'report', 'member_document'];

const AdminDocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({
    title: '', document_type: 'report', description: '', is_public: false
  });

  const fetchData = async () => {
    try {
      const response = await documentsAPI.getAll();
      setDocuments(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await documentsAPI.create(formData);
      toast.success('Document created');
      setShowModal(false);
      setFormData({ title: '', document_type: 'report', description: '', is_public: false });
      fetchData();
      // If file was selected, upload it
      if (selectedFile && response.data.id) {
        setUploadingFor(response.data.id);
      }
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleFileUpload = async (docId, file) => {
    try {
      await documentsAPI.upload(docId, file);
      toast.success('File uploaded');
      fetchData();
    } catch (error) {
      toast.error('Upload failed');
    }
    setUploadingFor(null);
    setSelectedFile(null);
  };

  const handleDownload = async (doc) => {
    try {
      const response = await documentsAPI.download(doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename || `${doc.title}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Download failed');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-documents-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl text-[#1A1614]">Documents</h1>
            <p className="text-[#8A847A]">{documents.length} total documents</p>
          </div>
          <Button onClick={() => setShowModal(true)} className="bg-[#7B1818] hover:bg-[#5C1212] rounded-none" data-testid="add-document-button">
            <Plus size={18} className="mr-2" /> New Document
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : (
          <div className="bg-white border border-[#D8D2C4]">
            <table className="w-full data-table" data-testid="documents-table">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left">Document</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Visibility</th>
                  <th className="px-4 py-3 text-left">File</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map(doc => (
                  <tr key={doc.id} data-testid={`document-row-${doc.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <FileText size={20} className="text-[#7B1818]" />
                        <div>
                          <p className="font-medium text-[#1A1614]">{doc.title}</p>
                          {doc.description && <p className="text-sm text-[#8A847A] line-clamp-1">{doc.description}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#4A453E] capitalize">{doc.document_type?.replace('_', ' ')}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs ${doc.is_public ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                        {doc.is_public ? 'Public' : 'Private'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {doc.file_path ? (
                        <span className="text-green-600 text-sm">Attached</span>
                      ) : (
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            onChange={(e) => e.target.files[0] && handleFileUpload(doc.id, e.target.files[0])}
                          />
                          <span className="flex items-center gap-1 text-sm text-[#7B1818] hover:underline">
                            <Upload size={14} /> Upload
                          </span>
                        </label>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {doc.file_path && (
                        <button onClick={() => handleDownload(doc)} className="p-2 hover:bg-[#F5F2EB] text-[#4A453E]" data-testid={`download-${doc.id}`}>
                          <Download size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {documents.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-[#8A847A]">No documents found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">New Document</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="document-form">
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required className="rounded-none" data-testid="document-input-title" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Document Type</label>
                <Select value={formData.document_type} onValueChange={(v) => setFormData({...formData, document_type: v})}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} className="rounded-none" />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={formData.is_public} onCheckedChange={(v) => setFormData({...formData, is_public: v})} />
                <label className="text-sm">Make document public</label>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-none">Cancel</Button>
                <Button type="submit" className="bg-[#7B1818] hover:bg-[#5C1212] rounded-none" data-testid="document-submit-button">Create Document</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminDocumentsPage;
